'use client';

import { Box, Link, List, ListItem, Typography } from '@mui/material';
import type React from 'react';
import { usePageAction } from '@repo/hooks/usePageAction';
import { LinkViewed } from '@repo/segment/Analytics/LinkViewed';
import type { Vertical } from '@repo/supergraph-schemas/marketplace/constants';
import type { RoundupAdditionalRecommendations } from '@repo/supergraph-schemas/roundup/base/types';

interface Props {
  additionalRecommendations: RoundupAdditionalRecommendations;
  vertical: Vertical;
}

export function AdditionalRecommendations({
  additionalRecommendations,
  vertical,
}: Props): React.JSX.Element | null {
  // TODO: Segment Standardization Cleanup
  const firePageAction = usePageAction({
    actionName: 'nw_review_redirected',
    actionSection: vertical,
    actionType: 'select',
  });

  if (!additionalRecommendations) return null;

  return (
    <Box
      data-testid="additional-recommendations"
      sx={{
        maxWidth: { xs: '100%', md: '82%' },
        margin: 'auto',
        mb: { xs: '1.75rem', md: '4rem' },
      }}
      component="section"
      aria-labelledby="additional-recommendations-title"
    >
      <Typography component="h2" id="additional-recommendations-title">
        Want to compare more options? Here are our other top picks:
      </Typography>
      <List sx={{ listStyleType: 'disc', listStylePosition: 'inside', pl: 2, py: 0 }}>
        {additionalRecommendations.map((review, index) => {
          const entity = review.marketplaceEntity;
          const { id, displayName, reviewLink, cta } = entity;
          const ctaLink = reviewLink || cta?.link;
          if (!ctaLink) return null;

          return (
            <ListItem key={`${id}-${index}`} sx={{ display: 'list-item', py: '4px', px: 0 }}>
              <LinkViewed
                entityName="nw_review_redirected"
                sectionName="banking"
                destinationUrl={ctaLink}
                linkCopy={displayName ?? ''}
                linkPosition={index}
              >
                {({ fireLinkClicked }) => (
                  <Link
                    href={ctaLink}
                    onClick={() => {
                      firePageAction();
                      fireLinkClicked();
                    }}
                    target="_blank"
                  >
                    {displayName}
                  </Link>
                )}
              </LinkViewed>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
